import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import { useModal } from 'hooks/useModal';
import { useQuery } from '@apollo/client';
import { unstable_batchedUpdates } from 'react-dom';
import { FormProvider } from 'react-hook-form';
import { ChargeBucketRef } from 'modules/equipment/types';
import { useEquipmentAssets } from 'modules/equipment/api';
import { Group } from 'components/shared/TableListing/types';
import { Equipment, useEquipmentActions } from 'modules/equipment';
import { Listing, useListing } from 'components/shared/TableListing';
import { QuoteCalcPreviewInput } from '@trnsact/trnsact-shared-types';
import { GraphqlResolverInput, ModalsKeys, Nullable, QueryResponse } from 'global';

import { quoteAPI } from '../../../api';
import { GroupSubtotalRow } from './cells';
import { ChargeListingService } from '../../../lib';
import { quoteMapper, quoteCalcMapper } from '../../../mappers';
import { GroupHeader, groupHeaderCellStyles } from './GroupHeader';
import { QuoteCalcNameSpace, QuoteCalcViewModel } from '../../../api/types';
import { invoiceDetailsListingColumns } from './invoiceDetailsListingConfig';
import {
  Quote,
  InvoiceListingActions,
  QuoteChargeListingEntity,
  QuoteChargeListingContext,
  QuoteLineItemType,
} from '../../../types';

interface Props {
  VOId: string;
  dynamicsVOId: string;
  quote: Nullable<Quote>;
  equipmentList: Equipment[];
  onSubscribe?: (fields: QuoteChargeListingEntity[]) => void;
  // Fires when rows are added or removed (not on field edits), so changes can be reacted to before the server calc
  onRowsChange?: (fields: QuoteChargeListingEntity[]) => void;
  onEquipmentUpdated?: () => void;
  // True while the calc does not yet reflect the current rows, so callers can hold saving
  onCalcPendingChange?: (isPending: boolean) => void;
  actionPanelBottom?: (actions: InvoiceListingActions) => React.ReactNode;
}

export const InvoiceDetailsListing = ({
  VOId,
  quote,
  onSubscribe,
  onRowsChange,
  onEquipmentUpdated,
  onCalcPendingChange,
  dynamicsVOId,
  equipmentList = [],
  actionPanelBottom,
}: Props) => {
  const [selectedImages, setSelectedImages] = useState<File[]>([]);

  const chargeService = useMemo(() => new ChargeListingService(), []);

  const { onDeleteEquipment, onUpdateEquipment, onCreateEquipment, isAnyActionInProgress } = useEquipmentActions();
  const { handleUploadImages } = useEquipmentAssets();

  const { handleOpen: openCustomChargeDialog } = useModal(ModalsKeys.CreateChargesEquipmentDialog);
  const { handleOpen: handleOpenChargesDialog, handleClose } = useModal(ModalsKeys.ChargesEquipmentDialog);
  const { handleOpen: handleOpenEquipmentDialog, handleClose: handleCloseEquipmentDialog } = useModal(
    ModalsKeys.CreateUpdateEquipmentDialog
  );

  const initialFields = useMemo(
    () => quoteMapper.toListingRows(VOId, equipmentList, quote),
    [VOId, equipmentList, quote]
  );

  const { form, columns, onRemove, tableData, handleInsertRow, handleRemoveRow, handleUpdateRow } = useListing<
    QuoteChargeListingEntity,
    QuoteChargeListingContext
  >({ headerColumns: invoiceDetailsListingColumns, bodyFields: initialFields }, { shouldUnregister: false });

  const onRowsChangeRef = useRef(onRowsChange);
  onRowsChangeRef.current = onRowsChange;

  const onSubscribeRef = useRef(onSubscribe);
  onSubscribeRef.current = onSubscribe;

  const onCalcPendingChangeRef = useRef(onCalcPendingChange);
  onCalcPendingChangeRef.current = onCalcPendingChange;

  const latestCalcRef = useRef<Nullable<QuoteCalcViewModel>>(null);

  const [debouncedSignature, setDebouncedSignature] = useState<string>(() =>
    quoteCalcMapper.toInputSignature(form.getValues('bodyFields') ?? [])
  );
  // True from the first edit until the debounce fires, i.e. while inputs differ from what the calc was requested for
  const [hasPendingInput, setHasPendingInput] = useState<boolean>(false);

  const debouncedSignatureRef = useRef(debouncedSignature);
  debouncedSignatureRef.current = debouncedSignature;

  const getFormRows = useCallback(
    () => (form.getValues('bodyFields') ?? []).filter((row): row is QuoteChargeListingEntity => Boolean(row)),
    [form]
  );

  // Edits are observed outside React state so typing does not re-render the table; state moves only on debounce
  useEffect(() => {
    let calcTimer: ReturnType<typeof setTimeout> | undefined;

    const handleChange = () => {
      const signature = quoteCalcMapper.toInputSignature(getFormRows());

      clearTimeout(calcTimer);

      if (signature === debouncedSignatureRef.current) {
        setHasPendingInput(false);
        return;
      }

      setHasPendingInput(true);
      calcTimer = setTimeout(() => {
        unstable_batchedUpdates(() => {
          setDebouncedSignature(signature);
          setHasPendingInput(false);
        });
      }, 400);
    };

    const subscription = form.watch(handleChange);

    return () => {
      subscription.unsubscribe();
      clearTimeout(calcTimer);
    };
  }, [form, getFormRows]);

  const previewRows = useMemo(
    () => quoteCalcMapper.toPreviewRows(form.getValues('bodyFields') ?? []),
    // eslint-disable-next-line
    [debouncedSignature]
  );

  const { data, previousData, loading, error } = useQuery<
    QuoteCalcNameSpace<QueryResponse<QuoteCalcViewModel>>,
    GraphqlResolverInput<QuoteCalcPreviewInput>
  >(quoteAPI.getQuoteCalcPreview, {
    skip: !previewRows.length,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        rows: previewRows,
      },
    },
  });

  // Calc for the latest request only, null while it is in flight; the only one safe to save
  const freshCalc = data?.quoteCalc?.result?.entity ?? null;
  // Last known calc, possibly stale (falls back to previousData); for display only, never for saving
  const serverCalc = (data ?? previousData)?.quoteCalc?.result?.entity ?? null;
  latestCalcRef.current = serverCalc;

  const isCalcPending = previewRows.length > 0 && (hasPendingInput || loading || (!freshCalc && !error));

  useEffect(() => {
    onCalcPendingChangeRef.current?.(isCalcPending);
  }, [isCalcPending]);

  const lastEmittedCalcRef = useRef<Nullable<QuoteCalcViewModel>>(null);

  // Computed prices are merged into the emitted rows only, so the form is not rewritten (and re-rendered) per row
  useEffect(() => {
    if (!freshCalc || hasPendingInput || lastEmittedCalcRef.current === freshCalc) return;

    lastEmittedCalcRef.current = freshCalc;
    onSubscribeRef.current?.(quoteCalcMapper.applyCalcToRows(form.getValues('bodyFields') ?? [], freshCalc));
  }, [freshCalc, hasPendingInput, form]);

  // Reports the current row set to the parent whenever rows are added, removed or replaced, not while typing
  useEffect(() => {
    onRowsChangeRef.current?.(quoteCalcMapper.applyCalcToRows(getFormRows(), latestCalcRef.current));
  }, [tableData, getFormRows]);

  const handleAddCharge = useCallback(
    (bucket: ChargeBucketRef) => {
      const equipmentCharges = quoteMapper.getChargesFromEquipment(bucket, equipmentList);

      const bodyFieldsValue = form.getValues('bodyFields');

      const equipment = equipmentList.find(item => item.equipmentId === bucket.id);
      const optionNames = new Set((equipment?.options ?? []).map(option => option.name));

      const isOptionRowInBucket = (row: QuoteChargeListingEntity): boolean =>
        row.type === QuoteLineItemType.Invoice &&
        row.charge?.groupBucket?.id === bucket.id &&
        optionNames.has(row.charge?.label ?? '');

      // Pre-check options that are already applied to the equipment
      const preselectedOptionLabels = (bodyFieldsValue ?? []).filter(isOptionRowInBucket).map(row => row.charge!.label);

      const resolveInsertIndex = () => chargeService.getInsertIndex(form.getValues('bodyFields') ?? [], bucket);

      handleOpenChargesDialog({
        ...(equipmentCharges ? { externalCharges: equipmentCharges } : {}),
        preselectedOptionLabels,
        onOpenCreateChargeDialog: () => {
          openCustomChargeDialog({
            onConfirm: newCharge => {
              const newRow = chargeService.createCustomChargeRow(bucket, newCharge.name);

              handleInsertRow(newRow, resolveInsertIndex(), { shouldFocus: false });
              handleClose();
            },
          });
        },
        onConfirm: charges => {
          handleClose();

          const selectedOptionLabels = new Set(
            charges.filter(charge => optionNames.has(charge.label)).map(charge => charge.label)
          );

          // Remove previously applied options that were unchecked in the dialog
          const removalIndexes = (form.getValues('bodyFields') ?? []).reduce<number[]>((acc, row, index) => {
            if (isOptionRowInBucket(row) && !selectedOptionLabels.has(row.charge!.label)) acc.push(index);
            return acc;
          }, []);
          removalIndexes.sort((a, b) => b - a).forEach(index => onRemove(index));

          // Skip options that are still applied so they are not duplicated
          const appliedOptionLabels = new Set(
            (form.getValues('bodyFields') ?? []).filter(isOptionRowInBucket).map(row => row.charge!.label)
          );

          let insertAt = resolveInsertIndex();

          charges.forEach(template => {
            const isOption = optionNames.has(template.label);
            if (isOption && appliedOptionLabels.has(template.label)) return;

            const newRow = chargeService.createChargeRowFromTemplate(
              bucket,
              isOption ? { ...template, isEquipmentOption: true } : template
            );

            handleInsertRow(newRow, insertAt, { shouldFocus: false });
            insertAt += 1;
          });
        },
      });
    },
    [
      equipmentList,
      form,
      handleOpenChargesDialog,
      chargeService,
      openCustomChargeDialog,
      handleInsertRow,
      handleClose,
      onRemove,
    ]
  );

  const handleAddEquipment = () => {
    handleOpenEquipmentDialog({
      equipmentId: '',
      onCancel: handleCloseEquipmentDialog,
      onImagesSaved: setSelectedImages,
      onSubmit: async ({ createDTO }) => {
        handleCloseEquipmentDialog();

        const equipmentId = await onCreateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...createDTO,
        });

        // Upload images if any were selected
        if (selectedImages.length > 0 && equipmentId) {
          await handleUploadImages(equipmentId, 'IMAGE', selectedImages);
          setSelectedImages([]); // Clear after upload
        }

        handleInsertRow(quoteMapper.toListingRow({ equipmentId, ...createDTO }, quote), 0, { shouldFocus: false });
      },
    });
  };

  const handleEditEquipment = (equipmentId: string, rowIndex: number) => {
    handleOpenEquipmentDialog({
      equipmentId,
      onCancel: handleCloseEquipmentDialog,
      onSubmit: async ({ updateDTO }) => {
        handleCloseEquipmentDialog();

        await onUpdateEquipment({
          vendorOpportunityId: VOId,
          dynamicsVendorOpportunityId: dynamicsVOId,
          ...updateDTO,
        });

        handleUpdateRow(quoteMapper.toUpdatedListingRow(form.getValues(`bodyFields.${rowIndex}`), updateDTO), rowIndex);
        onEquipmentUpdated?.();
      },
    });
  };

  const handleRemoveEquipment = (equipmentId: string, rowIndex: number) => {
    onDeleteEquipment(
      {
        equipmentId,
        vendorOpportunityId: VOId,
        dynamicsVendorOpportunityId: dynamicsVOId,
      },
      () => {
        const rows = form.getValues('bodyFields') ?? [];

        const indexesToRemove = rows.reduce<number[]>((acc, row, index) => {
          const isOptionOfEquipment =
            row.type === QuoteLineItemType.Invoice &&
            !!row.charge?.isEquipmentOption &&
            row.charge.groupBucket?.id === equipmentId;

          if (isOptionOfEquipment || index === rowIndex) acc.push(index);
          return acc;
        }, []);

        onRemove(indexesToRemove);
      }
    );
  };

  const groups = useMemo<Group<QuoteChargeListingEntity>[]>(() => {
    if (!tableData.length) return [];

    return chargeService.buildBuckets(tableData, VOId).map(bucket => ({
      name: bucket.id,
      data: bucket.data,
      beforeGroup: {
        cellStyles: groupHeaderCellStyles,
        render: () => <GroupHeader title={bucket.title} onAdd={() => handleAddCharge(bucket)} />,
      },
      afterGroup: {
        disableCellWrap: true,
        render: () => <GroupSubtotalRow data={bucket.data} columns={columns} priceCalc={serverCalc} />,
      },
    }));
  }, [VOId, tableData, chargeService, handleAddCharge, columns, serverCalc]);

  return (
    <FormProvider {...form}>
      <Listing<QuoteChargeListingEntity, QuoteChargeListingContext>
        groups={groups}
        loading={loading}
        columns={columns}
        tableData={tableData}
        injectContext={{
          form,
          chargeService,
          invoiceId: VOId,
          priceCalc: serverCalc,
          onRemoveRow: handleRemoveRow,
          onEditEquipment: handleEditEquipment,
          onRemoveEquipment: handleRemoveEquipment,
          isAnyEquipmentActionInProgress: isAnyActionInProgress,
        }}
        actionPanelBottom={() => actionPanelBottom?.({ onAddEquipment: handleAddEquipment })}
      />
    </FormProvider>
  );
};
